import { toCountryAlpha2 } from './country-code';

const US_CANADA_TIME_ZONES = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
  'America/St_Johns',
  'America/Halifax',
  'America/Glace_Bay',
  'America/Moncton',
  'America/Goose_Bay',
  'America/Toronto',
  'America/Iqaluit',
  'America/Winnipeg',
  'America/Resolute',
  'America/Rankin_Inlet',
  'America/Regina',
  'America/Swift_Current',
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Inuvik',
  'America/Dawson_Creek',
  'America/Fort_Nelson',
  'America/Whitehorse',
  'America/Dawson',
  'America/Vancouver',
  'America/Atikokan',
  'America/Blanc-Sablon',
  'America/Creston',
  'America/Montreal',
  'America/Nipigon',
  'America/Thunder_Bay',
  'America/Rainy_River',
  'America/Pangnirtung',
  'America/Yellowknife',
] as const;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

type TimeZoneNameStyle = 'short' | 'shortOffset' | 'longOffset';

const formatterFor = (
  timeZone: string,
  timeZoneName?: TimeZoneNameStyle,
): Intl.DateTimeFormat => {
  const key = `${timeZone}|${timeZoneName ?? ''}`;
  const cached = formatterCache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName,
  });
  formatterCache.set(key, formatter);
  return formatter;
};

const canonicalTimeZone = (timeZone: string): string | null => {
  try {
    return formatterFor(timeZone).resolvedOptions().timeZone;
  } catch {
    return null;
  }
};

const US_CANADA_CANONICAL_TIME_ZONES = new Set(
  US_CANADA_TIME_ZONES.map((zone) => canonicalTimeZone(zone)),
);

export const isKnownTimeZone = (timeZone: string): boolean =>
  canonicalTimeZone(timeZone) !== null;

export const isUsOrCanadaTimeZone = (timeZone: string): boolean => {
  const canonical = canonicalTimeZone(timeZone);
  return canonical !== null && US_CANADA_CANONICAL_TIME_ZONES.has(canonical);
};

const zoneNamePart = (
  instant: Date,
  timeZone: string,
  timeZoneName: TimeZoneNameStyle,
): string =>
  formatterFor(timeZone, timeZoneName)
    .formatToParts(instant)
    .find((part) => part.type === 'timeZoneName')?.value ?? '';

const LETTERS_ONLY = /^[A-Z]+$/;
const GMT_PREFIX = /^GMT/;
const LONG_OFFSET = /^GMT([+-])(\d{2}):(\d{2})$/;
const ALPHA_2 = /^[A-Z]{2}$/;
const LETTER_ABBREVIATION_COUNTRIES = new Set(['US', 'CA']);
const MINUTES_PER_HOUR = 60;

export const utcOffsetMinutesAt = (instant: Date, timeZone: string): number => {
  const match = LONG_OFFSET.exec(zoneNamePart(instant, timeZone, 'longOffset'));
  if (match === null) {
    return 0;
  }
  const [, sign, hours, minutes] = match;
  const total = Number(hours) * MINUTES_PER_HOUR + Number(minutes);
  return sign === '-' ? -total : total;
};

const usesLetterAbbreviation = (
  timeZone: string,
  country: string | null | undefined,
): boolean => {
  const code = toCountryAlpha2(country);
  return ALPHA_2.test(code)
    ? LETTER_ABBREVIATION_COUNTRIES.has(code)
    : isUsOrCanadaTimeZone(timeZone);
};

export const zoneLabelAt = (
  instant: Date,
  timeZone: string,
  country?: string | null,
): string => {
  const abbreviation = zoneNamePart(instant, timeZone, 'short');
  if (
    usesLetterAbbreviation(timeZone, country) &&
    LETTERS_ONLY.test(abbreviation)
  ) {
    return abbreviation;
  }
  return zoneNamePart(instant, timeZone, 'shortOffset').replace(
    GMT_PREFIX,
    'UTC',
  );
};
